import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { UploadFile } from '@mui/icons-material'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { documentoService } from '@/services/documentoService'
import { CurrencyField } from '@/components/common/CurrencyField'
import { formatarCnpj, normalizarCnpj } from '@/utils/cnpj'
import {
  FonteSituacaoFiscal,
  NotaFiscalComissao,
  NotaFiscalExtracao,
  SituacaoFiscalNota,
} from '@/types/comissao'
import { brl } from './estadoFinanceiro'
import { mensagemErroNota } from './errosNotaFiscal'
import { motivoArquivoNaoLiberado } from './verificacaoArquivoNf'
import { ROTULO_SITUACAO } from './notaFiscalRotulos'

/**
 * Entidade do documento de EVIDÊNCIA da situação fiscal. Não é `NOTA_FISCAL_COMISSAO` de
 * propósito: aquele tipo dispara a leitura automática da nota no originei-ia, e a evidência
 * (print do portal da prefeitura, por exemplo) não é a nota.
 */
export const TIPO_ENTIDADE_EVIDENCIA_NF = 'NOTA_FISCAL_COMISSAO_EVIDENCIA'

/** Selo de onde veio o dado lido (AN-9 §1). */
export const SeloLeitura: FC<{ extracao?: NotaFiscalExtracao | null }> = ({ extracao }) => {
  if (!extracao || extracao.status !== 'CONCLUIDA') {
    return <Chip size="small" variant="outlined" label="Sem leitura" />
  }
  if (extracao.origem === 'XML') {
    return <Chip size="small" color="success" label="Lido do XML" />
  }
  return <Chip size="small" color="info" variant="outlined" label="Lido por OCR + IA" />
}

/**
 * Sugestão inicial da situação: a do backend (`situacaoFiscalSugerida`, derivada da leitura);
 * sem ela, "Não verificada". O Financeiro confirma ou corrige — a tela não interpreta o XML.
 */
const situacaoSugerida = (e?: NotaFiscalExtracao | null): SituacaoFiscalNota =>
  (e?.status === 'CONCLUIDA' ? e.situacaoFiscalSugerida : null) ?? 'NAO_VERIFICADA'

const mes = (iso?: string | null) => (iso ? dayjs(iso).format('YYYY-MM') : '')
const dia = (iso?: string | null) => (iso ? dayjs(iso).format('YYYY-MM-DD') : '')

interface Props {
  /** Nota a conferir; nulo fecha. */
  nota: NotaFiscalComissao | null
  onFechar: () => void
  /** Conferência gravada — devolve a nota como o backend respondeu (com pendências). */
  onConferida: (nota: NotaFiscalComissao) => void
}

/**
 * "Conferir" (Financeiro, AN-9 §3): confirmação HUMANA dos dados da nota — lido × confirmado,
 * com o selo XML / OCR + IA. Leitura que falhou ou não reconheceu nada = conferência manual.
 * A situação fiscal é atestada aqui (Q-AN9-6): "Não verificada" bloqueia o pagamento.
 *
 * Nada é comparado na tela: o backend roda o motor de divergências na conferência e devolve as
 * pendências abertas.
 */
const ConferirNotaDialog: FC<Props> = ({ nota, onFechar, onConferida }) => {
  const [atual, setAtual] = useState<NotaFiscalComissao | null>(nota)
  const [carregando, setCarregando] = useState(false)

  const [numero, setNumero] = useState('')
  const [dataEmissao, setDataEmissao] = useState('')
  const [competencia, setCompetencia] = useState('')
  const [valorServicos, setValorServicos] = useState(0)
  const [emitenteCnpj, setEmitenteCnpj] = useState('')
  const [tomadorCnpj, setTomadorCnpj] = useState('')
  const [situacao, setSituacao] = useState<SituacaoFiscalNota>('NAO_VERIFICADA')
  const [fonte, setFonte] = useState<FonteSituacaoFiscal>('CONFERENCIA_MANUAL')
  const [evidencia, setEvidencia] = useState<File | null>(null)
  const [confirmarCnpj, setConfirmarCnpj] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const preencher = (n: NotaFiscalComissao) => {
    const c = n.conferencia
    const e = n.extracao?.status === 'CONCLUIDA' ? n.extracao : null
    setNumero(c?.numero ?? e?.numero ?? n.numero)
    setDataEmissao(dia(c?.dataEmissao ?? e?.dataEmissao ?? n.dataEmissao))
    setCompetencia(mes(c?.competencia ?? e?.competencia ?? null))
    setValorServicos(c?.valorServicos ?? e?.valorServicos ?? n.valor)
    setEmitenteCnpj(c?.emitenteCnpj ?? e?.emitenteCnpj ?? '')
    setTomadorCnpj(c?.tomadorCnpj ?? e?.tomadorCnpj ?? '')
    setSituacao(c?.situacaoFiscal ?? situacaoSugerida(n.extracao))
    setFonte(c?.situacaoFonte ?? (e?.origem === 'XML' ? 'XML' : 'CONFERENCIA_MANUAL'))
  }

  useEffect(() => {
    setAtual(nota)
    setErro(null)
    setEvidencia(null)
    setConfirmarCnpj(false)
    if (!nota) return
    preencher(nota)
    let vivo = true
    setCarregando(true)
    comissaoService
      .notaFiscal(nota.id)
      .then((n) => {
        if (!vivo) return
        setAtual(n)
        preencher(n)
      })
      .catch(() => undefined) // fica com a versão da lista
      .finally(() => vivo && setCarregando(false))
    return () => {
      vivo = false
    }
  }, [nota])

  const e = atual?.extracao?.status === 'CONCLUIDA' ? atual.extracao : null
  const leituraXml = e?.origem === 'XML'
  const manual = !e
  const completo =
    !!numero.trim() && !!dataEmissao && !!competencia && valorServicos > 0

  const conferir = async () => {
    if (!atual || !completo) return
    setSalvando(true)
    setErro(null)
    try {
      let evidenciaId: string | null = null
      if (evidencia) {
        const doc = await documentoService.upload(
          evidencia,
          'OUTRO',
          atual.id,
          TIPO_ENTIDADE_EVIDENCIA_NF
        )
        evidenciaId = doc.id
      }
      const conferida = await comissaoService.conferirNota(atual.id, {
        numero: numero.trim(),
        dataEmissao,
        competencia: `${competencia}-01`,
        valorServicos,
        emitenteCnpj: normalizarCnpj(emitenteCnpj) || null,
        tomadorCnpj: normalizarCnpj(tomadorCnpj) || null,
        situacaoFiscal: situacao,
        situacaoFonte: fonte,
        situacaoEvidenciaDocumentoId: evidenciaId,
        confirmarCnpjPrestador: confirmarCnpj,
      })
      onConferida(conferida)
    } catch (err) {
      setErro(mensagemErroNota(err, 'Falha ao registrar a conferência.'))
    } finally {
      setSalvando(false)
    }
  }

  const lido = (v: string | null | undefined) => (v ? v : '—')
  /** Onda 5: arquivo em verificação/recusado — o servidor responderia 409. */
  const arquivoBloqueado = atual ? motivoArquivoNaoLiberado(atual) : null

  return (
    <Dialog open={nota !== null} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>
        Conferir nota — {atual?.numero} <SeloLeitura extracao={atual?.extracao} />
      </DialogTitle>
      <DialogContent dividers>
        {carregando && !atual ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress aria-label="Carregando a nota" />
          </Box>
        ) : (
          <Stack spacing={2}>
            {erro && <Alert severity="error">{erro}</Alert>}
            {arquivoBloqueado && <Alert severity="warning">{arquivoBloqueado}</Alert>}
            {manual ? (
              <Alert severity="warning">
                A leitura automática não trouxe dados ({atual?.extracao?.mensagem ?? 'sem leitura'}).
                Esta é uma conferência manual: confira cada campo no arquivo da nota.
              </Alert>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Confira cada campo no arquivo e corrija o que a leitura trouxe errado. As
                divergências viram pendências quando você registrar a conferência — o
                pagamento fica bloqueado até elas se resolverem.
              </Typography>
            )}

            <Table size="small" aria-label="Lido e confirmado">
              <TableHead>
                <TableRow>
                  <TableCell>Campo</TableCell>
                  <TableCell>Informado pelo consultor</TableCell>
                  <TableCell>Lido do arquivo</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Confirmado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Número</TableCell>
                  <TableCell>{atual?.numero}</TableCell>
                  <TableCell>{lido(e?.numero)}</TableCell>
                  <TableCell>
                    <TextField size="small" label="Número confirmado" value={numero} onChange={(ev) => setNumero(ev.target.value)} fullWidth />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Emissão</TableCell>
                  <TableCell>{atual ? dayjs(atual.dataEmissao).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell>{e?.dataEmissao ? dayjs(e.dataEmissao).format('DD/MM/YYYY') : '—'}</TableCell>
                  <TableCell>
                    <TextField size="small" type="date" label="Emissão confirmada" value={dataEmissao} onChange={(ev) => setDataEmissao(ev.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Competência</TableCell>
                  <TableCell>{atual?.competenciasCobertas.map((c) => dayjs(c).format('MM/YYYY')).join(', ') || '—'}</TableCell>
                  <TableCell>{e?.competencia ? dayjs(e.competencia).format('MM/YYYY') : '—'}</TableCell>
                  <TableCell>
                    <TextField size="small" type="month" label="Competência confirmada" value={competencia} onChange={(ev) => setCompetencia(ev.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Valor dos serviços</TableCell>
                  <TableCell>{brl(atual?.valor)}</TableCell>
                  <TableCell>{brl(e?.valorServicos)}</TableCell>
                  <TableCell>
                    <CurrencyField size="small" label="Valor confirmado" value={valorServicos} onChange={setValorServicos} fullWidth />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>CNPJ do prestador</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>{e?.emitenteCnpj ? formatarCnpj(e.emitenteCnpj) : '—'}</TableCell>
                  <TableCell>
                    <TextField size="small" label="CNPJ do prestador confirmado" value={emitenteCnpj} onChange={(ev) => setEmitenteCnpj(ev.target.value)} fullWidth />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>CNPJ do tomador</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>{e?.tomadorCnpj ? formatarCnpj(e.tomadorCnpj) : '—'}</TableCell>
                  <TableCell>
                    <TextField size="small" label="CNPJ do tomador confirmado" value={tomadorCnpj} onChange={(ev) => setTomadorCnpj(ev.target.value)} fullWidth />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <FormControlLabel
              control={<Checkbox checked={confirmarCnpj} onChange={(ev) => setConfirmarCnpj(ev.target.checked)} />}
              label="Confirmo que o CNPJ do prestador é do consultor (passa a valer para as próximas notas)"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                select
                size="small"
                label="Situação fiscal"
                value={situacao}
                onChange={(ev) => setSituacao(ev.target.value as SituacaoFiscalNota)}
                sx={{ minWidth: 220 }}
              >
                {(Object.keys(ROTULO_SITUACAO) as SituacaoFiscalNota[]).map((s) => (
                  <MenuItem key={s} value={s}>
                    {ROTULO_SITUACAO[s]}
                  </MenuItem>
                ))}
              </TextField>
              <FormControl>
                <FormLabel id="fonte-situacao">Fonte da situação</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="fonte-situacao"
                  value={fonte}
                  onChange={(ev) => setFonte(ev.target.value as FonteSituacaoFiscal)}
                >
                  <FormControlLabel value="XML" control={<Radio />} label="XML da nota" disabled={!leituraXml} />
                  <FormControlLabel value="CONFERENCIA_MANUAL" control={<Radio />} label="Conferência manual" />
                </RadioGroup>
              </FormControl>
            </Stack>
            {situacao === 'NAO_VERIFICADA' && (
              <Alert severity="warning">
                Situação não verificada vira pendência do Financeiro e bloqueia o pagamento.
              </Alert>
            )}

            <Button component="label" variant="outlined" size="small" startIcon={<UploadFile />} sx={{ alignSelf: 'flex-start' }}>
              {evidencia ? evidencia.name : 'Anexar evidência da situação (opcional)'}
              <input
                hidden
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                aria-label="Evidência da situação fiscal"
                onChange={(ev) => setEvidencia(ev.target.files?.[0] ?? null)}
              />
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Voltar</Button>
        <Button
          variant="contained"
          onClick={() => void conferir()}
          disabled={salvando || !completo || !!arquivoBloqueado}
        >
          {salvando ? 'Registrando…' : 'Registrar conferência'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConferirNotaDialog
